import { Field, FieldRenderProps, useField } from 'react-final-form';
import { useFieldArray } from 'react-final-form-arrays';
import styled from 'styled-components';
import { BinIcon, typeToIcon, typeToLabel } from '../photos/constants';
import { NotesIconComponent } from '../photos';
import { useRemoveImage } from 'pages/ListerVehicleUploadV2/hooks/useRemoveImage';
import { useBackgroundImage } from 'pages/ListerVehicleUploadV2/hooks/useBackgroundImage';
import { useVehicleEditing } from 'context/isAnotherUserEditingContext';
import moment from 'moment';
import { ExpiredBanner } from './ExpiredBanner';

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 8px;
  padding: 0 8px 8px;
`;

const Image = styled.div<{ imageUrl: string; isPlaceholder: boolean }>`
  background-color: ${props => (props.isPlaceholder ? '#D9D9D9' : 'inherit')};
  background-image: ${({ imageUrl, isPlaceholder }) => (isPlaceholder ? 'none' : `url(${imageUrl})`)};
  background-size: 100% 100%;
  background-repeat: no-repeat;
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  align-items: center;
  justify-content: center;
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
`;

const TopImageRow = styled.div`
  padding: 8px;
  display: flex;
  width: 100%;
  margin-bottom: auto;
  justify-content: space-between;
`;

const TiresLabel = styled.div`
  font-weight: 400;
  font-size: 10px;
  line-height: 100%;
  color: #999999;
  padding: 0 12px;
  text-align: center;
  flex-basis: 25%;
`;

const PlaceholderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-basis: 75%;
  color: #999999;
`;

export const TiresAllPhotos = () => {
  const { fields } = useFieldArray(`exterior.subsections.tires.images`);

  const {
    input: { value: tires }
  } = useField(`exterior.subsections.tires`);

  return (
    <Wrapper>
      {Object.keys(tires.subsections).map(key => {
        if (key === 'tires_match_manufacturer') return null;
        const imgIndex = fields.value.findIndex(v => v.type === `tires.${key}`);
        return (
          <Field name={`exterior.subsections.tires.images.${imgIndex}`} key={key}>
            {fieldProps => <TiresImageComponent {...fieldProps} i={imgIndex} />}
          </Field>
        );
      })}
      {fields.value.map((value, i) => {
        if (!!value.type) return null;
        return (
          <Field name={`exterior.subsections.tires.images.${i}`}>
            {fieldProps => <TiresImageComponent {...fieldProps} i={i} />}
          </Field>
        );
      })}
    </Wrapper>
  );
};

const TiresImageComponent = ({ i, ...fieldProps }: FieldRenderProps<any, HTMLElement, any> & { i: number }) => {
  const removeImage = useRemoveImage();

  const { fields } = useFieldArray(`exterior.subsections.tires.images`);

  const handleRemove = async () => {
    const afterRemove = () => {
      fields.update(i, { ...fields.value[i], url: '', url_thumb: '' });
    };

    await removeImage(fieldProps.input.value.id, !!fieldProps.input.value.type, afterRemove);
  };

  const imageUrl = useBackgroundImage({
    localUrl: fieldProps.input.value.local_url,
    serverUrl: fieldProps.input.value.url,
    onServerLoaded: () => {
      if (fieldProps.input.value.local_url) {
        URL.revokeObjectURL(fieldProps.input.value.local_url);
        fieldProps.input.onChange({ ...(fieldProps.input.value || {}), local_url: '' });
      }
    }
  });

  const { hasAnotherEditor } = useVehicleEditing();

  const showExpiredBanner =
    fieldProps.input.value.expiration_date && moment().isAfter(fieldProps.input.value.expiration_date);

  return (
    <Image disabled={hasAnotherEditor} imageUrl={imageUrl} isPlaceholder={!imageUrl}>
      {!imageUrl && <PlaceholderWrapper>{typeToIcon[fieldProps.input.value.type]}</PlaceholderWrapper>}
      {!imageUrl && <TiresLabel>{typeToLabel[fieldProps.input.value.type]}</TiresLabel>}
      {imageUrl && (
        <TopImageRow>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={handleRemove}>
            <BinIcon />
          </div>
          <NotesIconComponent fieldName={fieldProps.input.name} />
        </TopImageRow>
      )}
      {showExpiredBanner && <ExpiredBanner />}
    </Image>
  );
};
